import React from "react";
import clsx from "clsx";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as VaultIcon }  from "../../assets/icons/vault_icon.svg";
import { ReactComponent as EventsIcon } from "../../assets/icons/events_icon.svg";
import castleLogo from "../../assets/icons/castle_logo.svg";
import { useTranslation } from "react-i18next";
import { useChatUnread } from "../../context/ChatUnreadContext";
import { useStatisticsUnread } from "../../context/StatisticsUnreadContext";
import { useWaitlistUnread } from "../../context/WaitlistUnreadContext";
import { useParticipantRequestUnread } from "../../context/ParticipantRequestUnreadContext";
import { useHostFeedbackUnread } from "../../context/HostFeedbackUnreadContext";
import { useClubMemberUnread } from "../../context/ClubMemberUnreadContext";
import { useClubRequestUnread } from "../../context/ClubRequestUnreadContext";
import { useMutualUnread } from "../../context/MutualUnreadContext";
import { useAppState } from "../../context/AppStateContext";
import {
  buildEventsNavigationState,
  getHostEventAttentionTotal,
} from "../../utils/eventHostAttention";

const BADGE_OFFSETS = {
  vault: { x: 8, y: -4 },
  events: { x: 8, y: -4 },
  search: { x: 8, y: -4 },
  clubs: { x: 8, y: -4 },
  chats: { x: 8, y: -4 },
};

const MENU_ASSET_BASE = "/assets/menu";
const CLUBS_NAV_WORDMARK_SRC = "/assets/clubs/clubs_nav_wordmark.svg";

const SideNavigation = ({
  className = "",
  isGuest = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const chatUnreadCtx = useChatUnread();
  const statisticsUnreadCtx = useStatisticsUnread();
  const waitlistUnreadCtx = useWaitlistUnread();
  const mutualUnreadCtx = useMutualUnread();

  const unreadTotalRaw =
    (chatUnreadCtx?.total_unread ??
      chatUnreadCtx?.totalUnread ??
      chatUnreadCtx?.unread_total ??
      chatUnreadCtx?.unreadTotal ??
      chatUnreadCtx?.total ??
      0);
  const unreadTotal = isGuest ? 0 : (Number(unreadTotalRaw) || 0);

  const unreadStatisticsRaw =
    (statisticsUnreadCtx?.unreadStatistics ??
      statisticsUnreadCtx?.unread_statistics ??
      statisticsUnreadCtx?.total_unread ??
      statisticsUnreadCtx?.totalUnread ??
      0);
  const unreadStatistics = isGuest ? 0 : (Number(unreadStatisticsRaw) || 0);

  const unreadWaitlistRaw =
    (waitlistUnreadCtx?.totalUnread ??
      waitlistUnreadCtx?.total_unread ??
      waitlistUnreadCtx?.unreadTotal ??
      waitlistUnreadCtx?.unread_total ??
      0);
  const unreadWaitlist = isGuest ? 0 : (Number(unreadWaitlistRaw) || 0);
  const participantRequestUnreadCtx = useParticipantRequestUnread();
  const unreadParticipantRequestsRaw =
    (participantRequestUnreadCtx?.totalUnread ??
      participantRequestUnreadCtx?.total_unread ??
      participantRequestUnreadCtx?.unreadTotal ??
      participantRequestUnreadCtx?.unread_total ??
      0);
  const unreadParticipantRequests = isGuest ? 0 : (Number(unreadParticipantRequestsRaw) || 0);
  const hostFeedbackUnreadCtx = useHostFeedbackUnread();
  const unreadHostFeedback = isGuest
    ? 0
    : (Number(hostFeedbackUnreadCtx?.totalUnread || 0) || 0);
  const hostEventAttentionTotal = getHostEventAttentionTotal(
    unreadWaitlist,
    unreadParticipantRequests,
    unreadHostFeedback
  );
  const eventsNavigationState = buildEventsNavigationState(
    unreadWaitlist,
    unreadParticipantRequests,
    unreadHostFeedback
  );
  const clubMemberUnreadCtx = useClubMemberUnread();
  const unreadClubMembers = isGuest ? 0 : (Number(clubMemberUnreadCtx?.totalUnread || 0) || 0);
  const unreadJoinedClubs = isGuest
    ? 0
    : (Number(clubMemberUnreadCtx?.joinedTotalUnread || 0) || 0);
  const clubRequestUnreadCtx = useClubRequestUnread();
  const pendingClubRequests = isGuest ? 0 : (Number(clubRequestUnreadCtx?.totalPending || 0) || 0);
  const unreadMutualRaw =
    (mutualUnreadCtx?.unreadCount ??
      mutualUnreadCtx?.unread_count ??
      mutualUnreadCtx?.total_unread ??
      0);
  const unreadMutual = isGuest ? 0 : (Number(unreadMutualRaw) || 0);
  const { hasFullVaultCoins } = useAppState();

  const guardClick = (e, name) => {
    if (isGuest && name !== "events" && name !== "clubs") {
      e.preventDefault();
      navigate("/auth", { replace: true });
    }
  };

  const activeTab = (() => {
    if (location.pathname.startsWith("/vault"))  return "vault";
    if (location.pathname.startsWith("/events")) return "events";
    if (location.pathname.startsWith("/clubs") || location.pathname.startsWith("/menu/clubs")) return "clubs";
    if (location.pathname.startsWith("/menu/chats")) return "chats";
    if (location.pathname.startsWith("/shop")) return "shop";
    if (location.pathname.startsWith("/menu/singles")) return "singles";
    if (location.pathname.startsWith("/menu/dinners")) return "dinners";
    if (location.pathname.startsWith("/search")) return "search";
    return "";
  })();

  const ACTIVE_COLOR = "#FFFFFF";
  const INACTIVE_COLOR = "rgba(255, 255, 255, 0.82)";

  const renderUnreadBadge = (count, offsets = BADGE_OFFSETS.chats) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;

    const label = n < 100 ? String(n) : "99+";
    const isWide = label.length >= 2;
    const isVeryWide = label.length >= 3;

    return (
      <span
        className={
          "absolute right-[4px] top-[6px] z-[4] font-semibold bg-white/90 text-[#1F74FF] " +
          (isVeryWide
            ? "text-[9px] h-[18px] min-w-[18px] px-[4px] "
            : isWide
            ? "text-[10px] h-[18px] min-w-[18px] px-[4px] "
            : "text-[12px] h-[18px] w-[18px] ") +
          "rounded-full flex items-center justify-center leading-none"
        }
        style={{ transform: `translate(${offsets?.x ?? 0}px, ${offsets?.y ?? 0}px)` }}
        aria-label={`Unread: ${label}`}
      >
        {label}
      </span>
    );
  };

  const navItems = [
    { name: "vault",  to: "/vault/main",   Icon: VaultIcon,  label: t("bottomNav.vault") },
    {
      name: "events",
      to: "/events/search",
      state: eventsNavigationState,
      Icon: EventsIcon,
      label: t("bottomNav.events"),
    },
    { name: "clubs",  to: "/clubs", asset: CLUBS_NAV_WORDMARK_SRC, assetClassName: "h-[24px] w-[24px]", label: t("bottomNav.clubs") },
    { name: "chats",  to: "/menu/chats", asset: `${MENU_ASSET_BASE}/chats_icon.svg`, assetClassName: "h-[21px] w-[21px]", label: t("bottomNav.chats", "Chats") },
    { name: "shop",   to: "/shop/garment", asset: `${MENU_ASSET_BASE}/shop_icon.svg`, assetClassName: "h-[22px] w-[20px]", label: t("bottomNav.shop") },
    { name: "singles", to: "/menu/singles", asset: `${MENU_ASSET_BASE}/singles_icon.svg`, assetClassName: "h-[24px] w-[24px]", label: t("bottomNav.singles", "Singles") },
    { name: "dinners", to: "/menu/dinners", asset: `${MENU_ASSET_BASE}/dinners_icon.svg`, assetClassName: "h-[21px] w-[24px]", label: t("bottomNav.dinners", "Dinners") },
    { name: "search", to: "/search/main", asset: `${MENU_ASSET_BASE}/search_icon.svg`, assetClassName: "h-[24px] w-[24px]", label: t("bottomNav.search") },
  ];
  const vaultBadgeCount = isGuest
    ? 0
    : unreadMutual > 0
      ? unreadMutual
      : hasFullVaultCoins
        ? 1
        : 0;

  return (
    <nav
      id="side-nav"
      className={clsx(
        "flex w-20 shrink-0 -translate-x-2 transform flex-col items-center pb-6 pt-6",
        className
      )}
    >
      <img src={castleLogo} alt="" width={60} height={49} className="mb-10 shrink-0" />
      <div className="flex min-h-0 flex-1 flex-col items-center justify-start gap-4 py-1">
        {navItems.map(({ name, to, state, Icon, asset, assetClassName, label }) => {
          const isActive = activeTab === name;
          return (
            <Link
              to={to}
              state={state}
              key={name}
              aria-label={label}
              title={label}
              className="relative flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full text-white no-underline transition duration-200 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              onClick={(e) => guardClick(e, name)}
            >
              <span
                className={clsx(
                  "absolute inset-0 rounded-full transition",
                  isActive
                    ? "bg-[radial-gradient(circle_at_42%_30%,#6EA7FF_0%,#4C8EFF_56%,#2F77F3_100%)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.22)]"
                    : "ring-1 ring-inset ring-white/25"
                )}
              />
              {asset ? (
                <img
                  src={asset}
                  alt=""
                  className={clsx(
                    "relative z-[2] object-contain transition-opacity",
                    assetClassName || "h-[22px] w-[22px]",
                    isActive ? "opacity-100" : "opacity-80"
                  )}
                />
              ) : (
                <Icon
                  className="relative z-[2] h-[22px] w-[22px]"
                  style={{ color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR }}
                />
              )}
              {name === "search" && !isGuest
                ? renderUnreadBadge(unreadStatistics, BADGE_OFFSETS.search)
                : null}
              {name === "vault" && !isGuest
                ? renderUnreadBadge(vaultBadgeCount, BADGE_OFFSETS.vault)
                : null}
              {name === "events" && !isGuest
                ? renderUnreadBadge(
                    hostEventAttentionTotal,
                    BADGE_OFFSETS.events
                  )
                : null}
              {name === "clubs" && !isGuest
                ? renderUnreadBadge(
                    unreadClubMembers + pendingClubRequests + unreadJoinedClubs,
                    BADGE_OFFSETS.clubs
                  )
                : null}
              {name === "chats" && !isGuest
                ? renderUnreadBadge(unreadTotal, BADGE_OFFSETS.chats)
                : null}
              <span className="sr-only">{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default SideNavigation;
